import { useMantineTheme } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';

/**
 * The whiteboard is shown on gym TVs, so on large screens (≥ lg) we enlarge text
 * and boxes for readability from afar; tablets and phones keep the compact layout.
 * Single source of truth so the details view and the results table stay in sync.
 */
export const useIsLargeDisplay = (): boolean => {
  const { breakpoints } = useMantineTheme();
  // `false` initial value so smaller screens never flash the enlarged layout.
  return useMediaQuery(`(min-width: ${breakpoints.lg})`, false);
};

// Approximate sticky nav height — keeps content that docks below it (the timer
// button, the sticky leaderboard panel) clear of the nav bar.
export const STICKY_NAV_OFFSET = 80;

/**
 * Enlarged sizes for the whiteboard / TV display. Single-value entries pair with a
 * Mantine size token on the compact side (e.g. `large ? sectionTitle : 'md'`);
 * `{ compact, large }` entries are used where both sides are raw px values.
 */
export const WHITEBOARD_SIZES = {
  workoutTitle: 60,
  sectionTitle: 44,
  sectionBadgeFont: 30,
  scoringLabel: 26,
  movementName: 36,
  scoreType: 28,
  notesTitle: 32,
  notesIcon: 40,
  bodyText: 32,
  resultsAthleteName: 30,
  resultsColumnTitle: 28,
  resultsColumnMeta: 20,
  sectionBadge: { compact: 32, large: 56 },
  contentMaxWidth: { compact: 900, large: 1500 },
  athleteColumn: { compact: 160, large: 220 },
  movementColumn: { compact: 200, large: 260 },
  weightMovementColumn: { compact: 280, large: 360 },
  rowDot: { compact: 8, large: 12 },
  leaderboardPanel: { compact: 360, large: 440 },
} as const;
